import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { buildTrain, TRAIN_Z } from "./tunnel/train";
import { buildGirl } from "./tunnel/girl";
import { buildProps } from "./tunnel/props";
import { doorChime, doorSlide } from "../lib/sound";
import { exitSignTex, stationSignTex, noSmokingTex, signMesh } from "./tunnel/signs";

let doorSounded = false;

const lerp = (a, b, t) => a + (b - a) * t;
const smooth = (a, b, x) => {
  const t = Math.min(Math.max((x - a) / (b - a), 0), 1);
  return t * t * (3 - 2 * t);
};

export default function TunnelScene({ progress }) {
  const mountRef = useRef(null);
  const progressRef = useRef(0);

  useEffect(() => {
    if (!progress) return undefined;
    return progress.on("change", (v) => {
      progressRef.current = v;
    });
  }, [progress]);

  const [assets, setAssets] = useState({});
  useEffect(() => {
    // Backend assets removed. Using defaults.
  }, []);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !assets) return;
    const A = (slot, fallback) => assets[slot] || fallback;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x06080a);
    scene.fog = new THREE.FogExp2(0x07090b, 0.055);

    const camera = new THREE.PerspectiveCamera(68, mount.clientWidth / mount.clientHeight, 0.1, 100);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    mount.appendChild(renderer.domElement);

    // fisheye: barrel warp normalised so corners never leave the frame
    const fisheyeShader = {
      uniforms: {
        tDiffuse: { value: null },
        strength: { value: 0.32 },
        aberration: { value: 0.55 },
      },
      vertexShader:
        "varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }",
      fragmentShader: [
        "uniform sampler2D tDiffuse;",
        "uniform float strength;",
        "uniform float aberration;",
        "varying vec2 vUv;",
        "void main(){",
        "  vec2 d = vUv - 0.5;",
        "  float r2 = dot(d, d);",
        "  float norm = 1.0 + strength * 0.5;",
        "  vec2 uv = 0.5 + d * (1.0 + strength * r2) / norm;",
        "  float ab = aberration * r2 * 0.06;",
        "  float cr = texture2D(tDiffuse, clamp(uv + d * ab, 0.0, 1.0)).r;",
        "  float cg = texture2D(tDiffuse, uv).g;",
        "  float cb = texture2D(tDiffuse, clamp(uv - d * ab, 0.0, 1.0)).b;",
        "  gl_FragColor = vec4(cr, cg, cb, 1.0);",
        "}",
      ].join("\n"),
    };
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    composer.addPass(new ShaderPass(fisheyeShader));

    // ---------- materials (photo textures) ----------
    const loader = new THREE.TextureLoader();
    const photo = (url, rx, ry) => {
      const t = loader.load(url);
      t.colorSpace = THREE.SRGBColorSpace;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(rx, ry);
      t.anisotropy = 8;
      return t;
    };
    const wallTex = photo(A("wall", "/textures/wall.jpg"), 5, 1.9);
    const wallMat = new THREE.MeshStandardMaterial({ map: wallTex, color: 0x9b9fa2, roughness: 0.6, metalness: 0.05 });
    const floorTex = photo(A("floor", "/textures/platform.jpg"), 2.4, 2.2);
    const floorMat = new THREE.MeshStandardMaterial({ map: floorTex, color: 0x9aa3a6, roughness: 0.9, metalness: 0.0 });
    const stepTex = photo(A("floor", "/textures/platform.jpg"), 2.4, 0.3);
    const stepMat = new THREE.MeshStandardMaterial({ map: stepTex, color: 0x9aa3a6, roughness: 0.9 });
    const ceilTex = photo(A("ceiling", "/textures/ceiling.jpg"), 2, 4);
    const concreteMat = new THREE.MeshStandardMaterial({ map: ceilTex, color: 0x6c7a80, roughness: 0.95 });
    const steelMat = new THREE.MeshStandardMaterial({ color: 0x2a3338, roughness: 0.5, metalness: 0.6 });

    const disposables = [wallTex, floorTex, stepTex, ceilTex, wallMat, floorMat, stepMat, concreteMat, steelMat];
    const track = (mesh) => {
      disposables.push(mesh.geometry);
      return mesh;
    };

    // ---------- architecture ----------
    const platform = track(new THREE.Mesh(new THREE.BoxGeometry(6.4, 0.4, 3), floorMat));
    platform.position.set(0, 2.6, 4.8);
    scene.add(platform);

    const stepGeo = new THREE.BoxGeometry(6, 0.42, 0.74);
    const nosingGeo = new THREE.BoxGeometry(6, 0.035, 0.05);
    const nosingMat = new THREE.MeshStandardMaterial({
      color: 0x1a4a55,
      emissive: 0x0e3742,
      emissiveIntensity: 0.7,
      roughness: 0.8,
    });
    disposables.push(stepGeo, nosingGeo, nosingMat);
    for (let i = 0; i < 9; i++) {
      const step = new THREE.Mesh(stepGeo, stepMat);
      step.position.set(0, 2.8 - (i + 1) * 0.311 - 0.21, 3.1 - i * 0.72);
      scene.add(step);
      const nosing = new THREE.Mesh(nosingGeo, nosingMat);
      nosing.position.set(0, 2.8 - (i + 1) * 0.311 + 0.012, 3.1 - i * 0.72 + 0.36);
      scene.add(nosing);
    }

    const landing = track(new THREE.Mesh(new THREE.BoxGeometry(6.4, 0.2, 6), floorMat));
    landing.position.set(0, -0.1, -5.9);
    scene.add(landing);

    const wallGeo = new THREE.BoxGeometry(0.3, 5.6, 15);
    disposables.push(wallGeo);
    const wallL = new THREE.Mesh(wallGeo, wallMat);
    wallL.position.set(-3.05, 2.5, -1.4);
    scene.add(wallL);
    const wallR = new THREE.Mesh(wallGeo, wallMat);
    wallR.position.set(3.05, 2.5, -1.4);
    scene.add(wallR);

    const ceiling = track(new THREE.Mesh(new THREE.BoxGeometry(6.6, 0.3, 15), concreteMat));
    ceiling.position.set(0, 5.25, -1.4);
    scene.add(ceiling);

    // wall above the train + the train itself (Tokyo car with sliding door)
    const upperWall = track(new THREE.Mesh(new THREE.BoxGeometry(6.6, 1.8, 0.3), wallMat));
    upperWall.position.set(0, 4.5, -9.2);
    scene.add(upperWall);
    const train = buildTrain(scene, disposables, A("steel", "/textures/steel.jpg"));
    buildProps(scene, disposables, camera, A("poster", "/textures/magazine.jpg"));

    // girl waiting by the door
    const girl = buildGirl(scene, disposables, {
      modelUrl: A("girl_model", "/models/girl.glb"),
      position: new THREE.Vector3(1.45, 0, -8.5),
      rotationY: -0.3,
      viewportHeight: mount.clientHeight,
    });

    // ---------- Tokyo signage ----------
    const exitTex = exitSignTex();
    const stationTex = stationSignTex();
    const smokeTexSign = noSmokingTex();
    disposables.push(exitTex, stationTex, smokeTexSign);

    const exitSign = signMesh(exitTex, 2.2, 0.44, 1.0);
    exitSign.position.set(0, 4.35, -1.0);
    exitSign.rotation.x = -0.18;
    scene.add(exitSign);
    disposables.push(exitSign.geometry, exitSign.material);
    const rodGeo = new THREE.CylinderGeometry(0.015, 0.015, 0.5, 6);
    disposables.push(rodGeo);
    [-0.9, 0.9].forEach((x) => {
      const rod = new THREE.Mesh(rodGeo, steelMat);
      rod.position.set(x, 4.85, -1.0);
      scene.add(rod);
    });

    const stationSign = signMesh(stationTex, 2.3, 0.79, 0.85);
    stationSign.position.set(2.88, 3.35, -6.4);
    stationSign.rotation.y = -Math.PI / 2;
    scene.add(stationSign);
    disposables.push(stationSign.geometry, stationSign.material);

    const noSmoking = signMesh(smokeTexSign, 1.0, 0.32, 0.9);
    noSmoking.position.set(2.88, 2.55, -8.35);
    noSmoking.rotation.y = -Math.PI / 2;
    scene.add(noSmoking);
    disposables.push(noSmoking.geometry, noSmoking.material);

    // ---------- lighting ----------
    scene.add(new THREE.AmbientLight(0x2a3034, 0.55));
    scene.add(new THREE.HemisphereLight(0x3a4448, 0x05070a, 0.28));

    const tubeMat1 = new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xf0f6f7, emissiveIntensity: 2.6 });
    const tubeMat2 = tubeMat1.clone();
    const tubeGeo = new THREE.BoxGeometry(1.9, 0.07, 0.16);
    disposables.push(tubeMat1, tubeMat2, tubeGeo);
    const tube1 = new THREE.Mesh(tubeGeo, tubeMat1);
    tube1.position.set(0, 5.05, 2.2);
    scene.add(tube1);
    const tube2 = new THREE.Mesh(tubeGeo, tubeMat2);
    tube2.position.set(0, 5.05, -3.6);
    scene.add(tube2);
    const tube3 = new THREE.Mesh(tubeGeo, tubeMat2);
    tube3.position.set(0, 5.05, -8.0);
    scene.add(tube3);

    const light1 = new THREE.PointLight(0xdfe9ec, 30, 12, 2);
    light1.position.set(0, 4.7, 2.2);
    scene.add(light1);
    const light2 = new THREE.PointLight(0xcfdde2, 28, 13, 2);
    light2.position.set(0, 4.7, -3.6);
    scene.add(light2);
    const pinkWash = new THREE.PointLight(0x22e07a, 5, 7, 2);
    pinkWash.position.set(0, 1.4, -7.4);
    scene.add(pinkWash);
    const lightDoor = new THREE.PointLight(0xdff4ff, 22, 9, 2);
    lightDoor.position.set(0, 4.6, -8.0);
    scene.add(lightDoor);

    const panelMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, emissive: 0xbdfcff, emissiveIntensity: 1.1 });
    const panelGeo = new THREE.PlaneGeometry(1.15, 0.38);
    disposables.push(panelMat, panelGeo);
    [
      { x: -2.88, ry: Math.PI / 2, z: 1.2 },
      { x: -2.88, ry: Math.PI / 2, z: -6.0 },
    ].forEach(({ x, ry, z }) => {
      const p = new THREE.Mesh(panelGeo, panelMat);
      p.position.set(x, 3.5, z);
      p.rotation.y = ry;
      scene.add(p);
      const pl = new THREE.PointLight(0x00f0ff, 6, 5, 2);
      pl.position.set(x * 0.85, 3.4, z);
      scene.add(pl);
    });

    // floating dust
    const N = 320;
    const pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 5.6;
      pos[i * 3 + 1] = Math.random() * 5;
      pos[i * 3 + 2] = 5 - Math.random() * 14;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const dustMat = new THREE.PointsMaterial({
      color: 0x00f0ff,
      size: 0.02,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    disposables.push(dustGeo, dustMat);
    const dust = new THREE.Points(dustGeo, dustMat);
    scene.add(dust);

    // ---------- interaction ----------
    let mx = 0;
    let my = 0;
    let smx = 0;
    let smy = 0;
    const onMove = (e) => {
      mx = e.clientX / window.innerWidth - 0.5;
      my = e.clientY / window.innerHeight - 0.5;
    };
    window.addEventListener("mousemove", onMove);

    let visible = true;
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    io.observe(mount);

    const clock = new THREE.Clock();
    let raf;
    let disp = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      if (!visible || document.hidden) return;
      const rawDt = clock.getDelta();
      const dt = Math.min(rawDt, 0.05);
      const t = clock.getElapsedTime();

      disp += (progressRef.current - disp) * (1 - Math.exp(-Math.min(rawDt, 0.5) * 4.2));
      smx += (mx - smx) * 0.06;
      smy += (my - smy) * 0.06;

      // phase 1: descend the stairs, phase 2: walk to the open door, phase 3: step in and turn down the aisle
      const d1 = Math.min(disp / 0.55, 1);
      const d2 = smooth(0.55, 0.8, disp);
      const d3 = smooth(0.8, 1.0, disp);
      const bob = Math.sin(t * 2.1) * 0.025 * Math.min(disp * 3, 1) * (1 - d3 * 0.6);
      const mouse = 1 - d2 * 0.7;
      const zWalk = d2 > 0 ? lerp(-2.4, -8.9, d2) : lerp(4.3, -2.4, d1);
      const z = d3 > 0 ? lerp(-8.9, TRAIN_Z - 1.38, d3) : zWalk;
      camera.position.set(smx * 0.35 * mouse * (1 - d3), lerp(3.6, 1.55, d1) + bob, z);
      // look target swings from the doorway to the far end of the car
      const tx = lerp(smx * 0.9 * mouse, 6.5, d3);
      const ty = lerp(lerp(lerp(0.0, 1.35, d1), 1.45, d2), 1.5, d3);
      const tz = lerp(lerp(lerp(-5.5, -8.8, d1), -10.6, d2), TRAIN_Z - 1.38, d3);
      camera.lookAt(tx, ty, tz);
      camera.rotation.x += smy * 0.05 * mouse * (1 - d3);

      const doorOpen = smooth(0.6, 0.76, disp);
      if (!doorSounded && doorOpen > 0.03) {
        doorSounded = true;
        doorChime();
        setTimeout(doorSlide, 700);
      }
      train.setOpen(doorOpen);
      girl.update(t, dt);

      const dip = Math.random() < 0.035 ? 0.2 + Math.random() * 0.45 : 1;
      light1.intensity = 30 * dip * (0.95 + Math.sin(t * 38) * 0.04);
      tubeMat1.emissiveIntensity = 2.6 * dip;
      light2.intensity = 28 * (0.94 + Math.sin(t * 23 + 2) * 0.06);

      dust.rotation.y = t * 0.012;
      composer.render();
    };
    loop();

    const onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      composer.setSize(mount.clientWidth, mount.clientHeight);
      girl.setViewport(mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
      disposables.forEach((d) => d.dispose && d.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [assets]);

  return <div ref={mountRef} data-testid="tunnel-scene" className="absolute inset-0" />;
}
